/**
 * StoreTracker - Progress by Store view
 * Shows all stores for a cycle with per-store campaign completion breakdown.
 */

import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TrackerLayout } from "./TrackerLayout";
import { EvidenceDrawer } from "./EvidenceDrawer";
import { getJson } from "@/lib/http";
import { trackerApiQueryString } from "@/lib/trackerTaskDefinitionFilter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from "@/components/ui/collapsible";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import type {
    TrackerStoreListItem,
    TrackerStoreCampaignRow,
    TrackerStoresResponse,
} from "@shared/tracker-types";
import type { CampaignStatusCount, CampaignStatusTaskType } from "@shared/tracker-campaign-status-counts";
import { isStoreEligibleForAdministrativeRecovery } from "@shared/admin-recovery-discrepancy";
import { ChevronDown, ChevronRight, Package, Trash2, Search, ExternalLink, CheckCheck, Clock, Check, Store as StoreIcon, AlertCircle, UserPlus, FolderOpen, Replace } from "lucide-react";
import { useLocation } from "wouter";
import { formatTrackerVisitDateTime } from "./dateFormatting";
import { StoreSignatureStatusLinks } from "@/components/admin/StoreSignatureStatusLinks";
import { useTrackerExportContext } from "@/hooks/useTrackerExportContext";
import {
    TrackerProgressExportButton,
    buildTrackerExportFilenameStem,
} from "@/components/tracker/TrackerProgressExportButton";
import { buildTrackerStoreProgressCsv } from "@/lib/trackerProgressExport";
import { isReviewPresenceEnabled } from "@/lib/reviewPresenceFlag";
import {
    groupReviewPresenceByStore,
    isReviewPresencePollEnabled,
    resolveReviewPresencePollInterval,
} from "@/lib/reviewPresenceByStore";
import type { ActiveReviewerPresence, ReviewPresenceGetResponse } from "@shared/review-presence-api";
import { StoreReviewPresence, StoreReviewPresenceTooltipRoot } from "@/components/tracker/StoreReviewPresence";

import type { StoreReviewWorkload } from '@shared/review-workload';
import { StoreReviewMarker } from '@/components/tracker/StoreReviewMarker';
import { sortStoresForReview, storeReviewHref, type StoreReviewSort } from '@/lib/storeReviewPriority';

const POLL_INTERVAL_ACTIVE = 30000;
const POLL_INTERVAL_BACKGROUND = 120000;

const STATUS_PILL = "inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap";

function OperationalBadge({ status }: { status: TrackerStoreListItem['operationalStatus'] }) {
    switch (status) {
        case 'NOT_STARTED':
            return (
                <span className={`${STATUS_PILL} bg-gray-100 text-gray-700`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" />
                    Not Started
                </span>
            );
        case 'IN_PROGRESS':
            return (
                <span className={`${STATUS_PILL} bg-blue-50 text-blue-700`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                    In Progress
                </span>
            );
        case 'COMPLETE':
            return (
                <span className={`${STATUS_PILL} bg-green-50 text-green-700`}>
                    <Check className="h-3 w-3 flex-shrink-0" />
                    Operator Complete
                </span>
            );
    }
}

function ComplianceBadge({ status }: { status: TrackerStoreListItem['complianceStatus'] }) {
    switch (status) {
        case 'NOT_STARTED':
            return (
                <span className={`${STATUS_PILL} bg-gray-100 text-gray-700`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" />
                    Not Started
                </span>
            );
        case 'IN_REVIEW':
            return (
                <span className={`${STATUS_PILL} bg-amber-50 text-amber-700`}>
                    <Clock className="h-3 w-3 flex-shrink-0" />
                    In Review
                </span>
            );
        case 'COMPLETE':
            return (
                <span className={`${STATUS_PILL} bg-green-50 text-green-700`}>
                    <CheckCheck className="h-3 w-3 flex-shrink-0" />
                    Compliance Complete
                </span>
            );
    }
}

function CampaignStatusBadge({ status }: { status: TrackerStoreCampaignRow["status"] }) {
    switch (status) {
        case "complete":
            return <Badge variant="success" className="text-xs">Complete</Badge>;
        case "partial":
            return <Badge variant="warning" className="text-xs">Partial</Badge>;
        case "issue":
            return <Badge variant="destructive" className="text-xs">Issue</Badge>;
        case "pending":
        default:
            return <Badge variant="outline" className="text-xs">Pending</Badge>;
    }
}

const TASK_TYPE_STATUS_LABEL: Record<CampaignStatusTaskType, string> = {
    POSTING: "Posts",
    REMOVAL: "Removals",
    REPOST: "Reposts",
};

function submissionCopy(type: CampaignStatusTaskType, counts: CampaignStatusCount) {
    switch (type) {
        case "POSTING":
            return `${counts.submitted} of ${counts.totalTasks} posted${counts.notSubmitted > 0 ? ` · ${counts.notSubmitted} not posted yet` : ""}`;
        case "REMOVAL":
            return `${counts.submitted} of ${counts.totalTasks} removed${counts.notSubmitted > 0 ? ` · ${counts.notSubmitted} not removed yet` : ""}`;
        case "REPOST":
            return `${counts.submitted} of ${counts.totalTasks} reposted${counts.notSubmitted > 0 ? ` · ${counts.notSubmitted} not reposted yet` : ""}`;
    }
}

function operatorTaskTypeStatus(counts: CampaignStatusCount) {
    if (counts.submitted === 0) return "operator not started";
    return counts.notSubmitted > 0 ? "operator incomplete" : "operator complete";
}

function adminTaskTypeStatus(counts: CampaignStatusCount) {
    if (counts.submitted === 0) return "admin not started";
    if (counts.awaitingReview > 0) return "admin in review";
    return "admin complete";
}

function taskTypeStatusLabel(counts: CampaignStatusCount) {
    return `${operatorTaskTypeStatus(counts)}, ${adminTaskTypeStatus(counts)}`;
}

function CampaignStatusLine({ type, counts }: { type: CampaignStatusTaskType; counts: CampaignStatusCount }) {
    return (
        <div className="py-1.5 border-t border-gray-100 first:border-t-0 first:pt-0 last:pb-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                {TASK_TYPE_STATUS_LABEL[type]}{" "}
                <span className={counts.notSubmitted > 0 || counts.awaitingReview > 0 ? "text-amber-700" : "text-green-700"}>
                    ({taskTypeStatusLabel(counts)})
                </span>
            </div>
            <div className="text-xs text-gray-700 mt-0.5">
                {submissionCopy(type, counts)}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs mt-0.5">
                <span className={counts.approved > 0 ? "text-green-700" : "text-gray-400"}>
                    {counts.approved} approved
                </span>
                <span className={counts.rejected > 0 ? "text-red-700 font-medium" : "text-gray-400"}>
                    {counts.rejected} rejected
                </span>
                <span className={counts.awaitingReview > 0 ? "text-amber-700 font-medium" : "text-gray-400"}>
                    {counts.awaitingReview} awaiting review
                </span>
            </div>
        </div>
    );
}

function CampaignStatusBreakdown({ store }: { store: TrackerStoreListItem }) {
    const types: CampaignStatusTaskType[] = ["POSTING", "REMOVAL", "REPOST"];
    const visible = types.filter(t => store.campaignStatusCountsByType[t].totalTasks > 0);

    if (visible.length === 0) {
        return <span className="text-xs text-muted-foreground">No tasks</span>;
    }

    return (
        <div className="rounded-md border border-gray-100 bg-white px-2.5 py-2 shadow-sm">
            {visible.map(t => (
                <CampaignStatusLine key={t} type={t} counts={store.campaignStatusCountsByType[t]} />
            ))}
        </div>
    );
}

function SummaryCards({ stores }: { stores: TrackerStoreListItem[] }) {
    const totalStores = stores.length;
    const started = stores.filter(s => s.operationalStatus !== "NOT_STARTED").length;
    const notStarted = totalStores - started;
    const operatorComplete = stores.filter(s => s.operationalStatus === "COMPLETE").length;
    const operatorAndComplianceComplete = stores.filter(
        s => s.operationalStatus === "COMPLETE" && s.complianceStatus === "COMPLETE",
    ).length;

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card
                label="Total Stores"
                value={totalStores.toString()}
                helpText="All stores in this selected cycle."
                icon={<StoreIcon className="h-4 w-4 text-muted-foreground" />}
            />
            <Card
                label="Started"
                value={started.toString()}
                helpText={`${notStarted} not started yet.`}
                icon={<Clock className="h-4 w-4 text-muted-foreground" />}
            />
            <Card
                label="Operator Complete"
                value={operatorComplete.toString()}
                helpText="Operator has completed all required submissions and reposts."
                icon={<CheckCheck className="h-4 w-4 text-muted-foreground" />}
            />
            <Card
                label="Operator + Compliance Complete"
                value={operatorAndComplianceComplete.toString()}
                helpText="Operator work is complete and admin review has no items awaiting review."
                icon={<Check className="h-4 w-4 text-muted-foreground" />}
            />
        </div>
    );
}

function Card({
    label,
    value,
    icon,
    helpText,
    extra,
    valueClassName = ""
}: {
    label: string;
    value: string;
    icon: React.ReactNode;
    helpText?: string;
    extra?: React.ReactNode;
    valueClassName?: string;
}) {
    return (
        <div className="bg-white border border-border rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
                {icon}
            </div>
            <div className={`text-2xl font-semibold ${valueClassName}`}>{value}</div>
            {helpText && (
                <div className="text-xs text-muted-foreground mt-1 leading-snug">
                    {helpText}
                </div>
            )}
            {extra}
        </div>
    );
}

const ROW_GRID = "grid grid-cols-[2.5rem_minmax(12rem,1.6fr)_8.5rem_10rem_28rem_11rem] gap-3 items-start";

function StoreRow({
    store,
    index,
    onViewEvidence,
    cycleId,
    retailerId,
    storeReviewers,
    showReviewPresence,
    reviewWorkload,
    reviewNow,
    reviewHref,
}: {
    store: TrackerStoreListItem;
    reviewWorkload?: StoreReviewWorkload;
    reviewNow: number;
    reviewHref: string;
    index: number;
    onViewEvidence: (store: TrackerStoreListItem) => void;
    cycleId: string | null;
    retailerId: string | null;
    storeReviewers: ActiveReviewerPresence[];
    showReviewPresence: boolean;
}) {
    const [, setLocation] = useLocation();
    const [open, setOpen] = useState(false);
    const postings = sortCampaignsByUrgency(store.campaigns.filter(c => c.taskType === "POSTING" || c.taskType === "REPOST"));
    const removals = sortCampaignsByUrgency(store.campaigns.filter(c => c.taskType === "REMOVAL"));
    const reworkPending = store.campaigns.filter(c => c.status === "issue" || (c.taskType === "REPOST" && c.status === "pending"));
    const reworkTotal = store.campaigns.filter(c => c.taskType === "REPOST" || c.status === "issue");
    const hasEvidence = !!store.routeItemId;
    const operatorOptions = store.operatorOptions ?? [];
    const showRecovery = operatorOptions.length > 0 && !!cycleId && isStoreEligibleForAdministrativeRecovery(store);
    const showSubstitutePosting = !!cycleId;
    const isMultiOperator = operatorOptions.length > 1;
    const substitutePostingHref = useMemo(() => {
        const params = new URLSearchParams();
        if (cycleId) params.set('cycleId', cycleId);
        if (retailerId) params.set('retailerId', retailerId);
        params.set('storeId', String(store.storeId));
        return `/admin/activation-cockpit/substitute-posting/store?${params.toString()}`;
    }, [cycleId, retailerId, store.storeId]);
    const pendingSentToLabel = !store.operatorName && store.status === "PENDING" && store.sentToOperatorName
        ? `Sent to: ${store.sentToOperatorName}`
        : null;
    const operatorLabel = store.operatorNames?.length
        ? `${store.operatorNames.length > 1 ? "Operators" : "Operator"}: ${store.operatorNames.join(", ")}`
        : store.operatorName
            ? `Operator: ${store.operatorName}`
            : pendingSentToLabel;

    return (
        <Collapsible
            open={open}
            onOpenChange={setOpen}
            className={[
                "transition-all",
                open
                    ? "relative z-10 bg-white border border-blue-300 rounded-lg shadow-lg my-2 mx-[-0.5rem]"
                    : "border-b last:border-b-0",
            ].join(" ")}
        >
            <CollapsibleTrigger asChild>
                <div
                    className={[
                        ROW_GRID,
                        "px-6 py-4 cursor-pointer transition-colors group",
                        open ? "" : "hover:bg-gray-50",
                    ].join(" ")}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen(o => !o); }}
                >
                    {/* # */}
                    <div className="text-sm text-muted-foreground tabular-nums">{index + 1}</div>

                    {/* Store Name (location now exposed via tooltip on hover) */}
                    <div className="min-w-0" title={store.storeAddress || undefined}>
                        <div className="font-medium text-sm truncate">{store.storeName}</div>
                        {operatorLabel && (
                            <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                {operatorLabel}
                            </div>
                        )}
                        <StoreReviewMarker workload={reviewWorkload} now={reviewNow} href={reviewHref} />
                    </div>

                    {/* Operator Status */}
                    <div className="flex items-center">
                        <OperationalBadge status={store.operationalStatus} />
                    </div>

                    {/* Compliance Status */}
                    <div className="flex flex-col items-start gap-1 min-w-0">
                        <ComplianceBadge status={store.complianceStatus} />
                        {showReviewPresence && (
                            <StoreReviewPresence reviewers={storeReviewers} />
                        )}
                    </div>

                    {/* Campaign Status — submissions + compliance review pipeline */}
                    <div className="min-w-0">
                        <CampaignStatusBreakdown store={store} />
                    </div>

                    {/* Actions — Evidence (top) + Admin (under), chevron centered to the right */}
                    <div className="flex items-center gap-2 justify-end">
                        <div className="flex flex-col items-stretch gap-1.5">
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50 justify-center"
                                disabled={!hasEvidence}
                                title={hasEvidence ? "View evidence & review" : "No visit recorded yet"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (hasEvidence) onViewEvidence(store);
                                }}
                            >
                                <FolderOpen className="h-3.5 w-3.5" />
                                Evidence
                            </Button>
                            {showRecovery && !isMultiOperator && (
                                <Button
                                    size="sm"
                                    className="h-8 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm justify-center"
                                    title="Complete outstanding tasks on behalf of the operator"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLocation(
                                            `/admin/activation-cockpit/recovery/store?cycleId=${cycleId}&storeId=${store.storeId}&assignedOperatorId=${operatorOptions[0]!.id}`,
                                        );
                                    }}
                                >
                                    <UserPlus className="h-3.5 w-3.5" />
                                    Admin
                                </Button>
                            )}
                            {showSubstitutePosting && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 gap-1.5 border-amber-300 text-amber-900 hover:bg-amber-50 justify-center"
                                    title="Record a substitute posting at this store (outside campaign plan)"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLocation(substitutePostingHref);
                                    }}
                                >
                                    <Replace className="h-3.5 w-3.5" />
                                    Substitute
                                </Button>
                            )}

                            {!showRecovery && <StoreSignatureStatusLinks cycleId={cycleId} storeId={store.storeId}
                                retailerId={retailerId} operators={operatorOptions}
                                missingOperatorIds={(store.operatorsMissingManagerSignoff ?? []).map(operator => operator.id)} />}

                            {showRecovery && isMultiOperator && (
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            size="sm"
                                            className="h-8 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm justify-center"
                                            title="Multi-operator store — choose which operator to act on behalf of"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <UserPlus className="h-3.5 w-3.5" />
                                            Admin
                                            <ChevronDown className="h-3 w-3 opacity-80" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align="end"
                                        className="w-64 p-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground px-2 pb-1.5">
                                            Act on behalf of
                                        </div>
                                        <div className="flex flex-col">
                                            {operatorOptions.map(op => (
                                                <button
                                                    key={op.id}
                                                    type="button"
                                                    className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-blue-50 text-left"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setLocation(
                                                            `/admin/activation-cockpit/recovery/store?cycleId=${cycleId}&storeId=${store.storeId}&assignedOperatorId=${op.id}`,
                                                        );
                                                    }}
                                                >
                                                    <UserPlus className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                                                    <span className="truncate">{op.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                        {open ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                    </div>
                </div>
            </CollapsibleTrigger>

            <CollapsibleContent>
                <div className="bg-gray-50 border-t border-blue-100 px-6 py-4 space-y-3 rounded-b-lg">
                    {reworkTotal.length > 0 && (
                        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 flex items-start gap-2.5">
                            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-red-800">
                                    {reworkPending.length === 0
                                        ? `All ${reworkTotal.length} re-work task${reworkTotal.length === 1 ? "" : "s"} completed`
                                        : `${reworkPending.length} of ${reworkTotal.length} re-work task${reworkTotal.length === 1 ? "" : "s"} still pending`}
                                </div>
                                {reworkPending.length > 0 && (
                                    <div className="text-xs text-red-700 mt-0.5">
                                        {reworkPending.slice(0, 3).map(r => r.campaignName).join(" · ")}
                                        {reworkPending.length > 3 ? ` · +${reworkPending.length - 3} more` : ""}
                                    </div>
                                )}
                            </div>
                            {hasEvidence && reworkPending.length > 0 && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 gap-1.5 border-red-300 text-red-700 hover:bg-red-100 bg-white"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onViewEvidence(store);
                                    }}
                                >
                                    Review
                                </Button>
                            )}
                        </div>
                    )}
                    {(store.storeAddress || store.checkInAt || store.checkOutAt) && (
                        <div className="text-xs text-muted-foreground space-y-0.5">
                            {store.storeAddress && (
                                <div className="truncate">{store.storeAddress}</div>
                            )}
                            {(store.checkInAt || store.checkOutAt) && (
                                <div>
                                    {store.checkInAt && <>In: {formatTrackerVisitDateTime(store.checkInAt)}</>}
                                    {store.checkInAt && store.checkOutAt && " · "}
                                    {store.checkOutAt && <>Out: {formatTrackerVisitDateTime(store.checkOutAt)}</>}
                                </div>
                            )}
                        </div>
                    )}
                    {postings.length > 0 && (
                        <div className="space-y-1">
                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1">
                                <Package className="h-3 w-3" /> Postings
                            </div>
                            {postings.map((c, i) => (
                                <CampaignLine key={i} campaign={c} />
                            ))}
                        </div>
                    )}
                    {removals.length > 0 && (
                        <div className="space-y-1">
                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1">
                                <Trash2 className="h-3 w-3" /> Removals
                            </div>
                            {removals.map((c, i) => (
                                <CampaignLine key={i} campaign={c} />
                            ))}
                        </div>
                    )}
                    {hasEvidence && (
                        <div className="pt-1">
                            <Button
                                size="sm"
                                variant="outline"
                                className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onViewEvidence(store);
                                }}
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                View full evidence &amp; photos
                            </Button>
                        </div>
                    )}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

function CampaignLine({ campaign }: { campaign: TrackerStoreCampaignRow }) {
    const { completedQuantity: delivered, expectedQuantity: planned } = campaign;
    const isShort = planned > 0 && delivered < planned;
    const isIssue = campaign.status === "issue";
    const isPending = campaign.status === "pending";
    const isRepost = campaign.taskType === "REPOST";

    const rowClasses = isIssue
        ? "border-l-4 border-red-400 bg-red-50/60 pl-2 pr-2 -mx-2 rounded"
        : isPending
            ? "border-l-4 border-amber-400 bg-amber-50/60 pl-2 pr-2 -mx-2 rounded"
            : "";

    return (
        <div className={`flex items-center gap-3 py-1.5 text-sm ${rowClasses}`}>
            <span className="flex-1 min-w-0">
                <span className="block truncate">
                    {campaign.campaignName}
                    {isRepost && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">
                            Re-work
                        </span>
                    )}
                </span>
                {campaign.operatorName && (
                    <span className="block text-xs text-muted-foreground truncate">{campaign.operatorName}</span>
                )}
            </span>
            {isShort && (
                <span className="text-xs tabular-nums text-amber-600">
                    {delivered} of {planned} units
                </span>
            )}
            <CampaignStatusBadge status={campaign.status} />
        </div>
    );
}

const CAMPAIGN_STATUS_ORDER: Record<TrackerStoreCampaignRow["status"], number> = {
    issue: 0,
    pending: 1,
    partial: 2,
    complete: 3,
};

function sortCampaignsByUrgency(campaigns: TrackerStoreCampaignRow[]): TrackerStoreCampaignRow[] {
    return [...campaigns].sort((a, b) => {
        const orderDiff = CAMPAIGN_STATUS_ORDER[a.status] - CAMPAIGN_STATUS_ORDER[b.status];
        if (orderDiff !== 0) return orderDiff;
        return a.campaignName.localeCompare(b.campaignName);
    });
}


type StoreStatusFilter =
    | "ALL"
    | "STARTED"
    | "NOT_STARTED"
    | "OPERATOR_COMPLETE"
    | "FULLY_COMPLETE"
    | "NEEDS_REVIEW";

export default function StoreTracker() {
    const searchParams = new URLSearchParams(window.location.search);
    const cycleId = searchParams.get("cycleId");
    const retailerId = searchParams.get("retailerId");
    const taskDefinitionIds = searchParams.get("taskDefinitionIds");
    const campaignId = searchParams.get('campaignId');

    const [reviewSort, setReviewSort] = useState<StoreReviewSort>('name');
    const [awaitingReviewOnly, setAwaitingReviewOnly] = useState(false);
    const [reviewNow, setReviewNow] = useState(Date.now());
    useEffect(() => {
        const timer = window.setInterval(() => setReviewNow(Date.now()), 60000);
        return () => window.clearInterval(timer);
    }, []);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<StoreStatusFilter>("ALL");
    const [hideAdminComplete, setHideAdminComplete] = useState(false);
    const [evidenceStore, setEvidenceStore] = useState<TrackerStoreListItem | null>(null);
    const queryClient = useQueryClient();

    const [isTabVisible, setIsTabVisible] = useState(!document.hidden);
    useEffect(() => {
        const handleVisibility = () => setIsTabVisible(!document.hidden);
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, []);

    const pollInterval = isTabVisible ? POLL_INTERVAL_ACTIVE : POLL_INTERVAL_BACKGROUND;

    const reviewPresenceFlagOn = isReviewPresenceEnabled();
    const presencePollEnabled = isReviewPresencePollEnabled(reviewPresenceFlagOn, cycleId);
    const presenceCycleId = presencePollEnabled && cycleId ? Number(cycleId) : null;
    const presencePollInterval = resolveReviewPresencePollInterval(isTabVisible);

    const { data: presenceData } = useQuery<ReviewPresenceGetResponse>({
        queryKey: ["/api/review/presence", presenceCycleId],
        queryFn: async () =>
            getJson<ReviewPresenceGetResponse>(`/api/review/presence?cycleId=${presenceCycleId}`),
        enabled: presencePollEnabled && presenceCycleId != null,
        refetchInterval: presencePollEnabled ? presencePollInterval : false,
        staleTime: 0,
        retry: 1,
    });

    const presenceByStore = useMemo(
        () => groupReviewPresenceByStore(presenceData?.reviewers ?? []),
        [presenceData?.reviewers],
    );

    const { data, isLoading, dataUpdatedAt, refetch, isFetching, error } = useQuery<TrackerStoresResponse>({
        queryKey: ["tracker-stores", cycleId, taskDefinitionIds, retailerId, campaignId],
        queryFn: async () => {
            const params = new URLSearchParams(trackerApiQueryString(taskDefinitionIds));
            if (retailerId) params.set('retailerId', retailerId);
            if (campaignId && campaignId !== 'all') params.set('campaignId', campaignId);
            return await getJson<TrackerStoresResponse>(`/api/analytics/tracker/stores/${cycleId}?${params}`);
        },
        enabled: !!cycleId,
        staleTime: 15000,
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
        refetchOnReconnect: 'always',
        refetchInterval: pollInterval
    });


    const stores = data?.stores || [];
    const workloadByStore = useMemo(() => new Map(
        (data?.reviewWorkload?.stores ?? []).map(item => [item.storeId, item]),
    ), [data?.reviewWorkload]);
    const workloadAvailable = data?.reviewWorkload != null;
    const reviewAgeNow = data?.reviewWorkload
        ? Date.parse(data.reviewWorkload.generatedAt) + Math.max(0, reviewNow - dataUpdatedAt)
        : reviewNow;
    const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null;

    const exportContext = useTrackerExportContext({
        reportLabel: "Progress by Store",
        retailerId,
        cycleId,
        taskDefinitionIds,
    });
    const storeExportCsv = useMemo(() => {
        if (!exportContext || stores.length === 0) {
            return null;
        }
        return buildTrackerStoreProgressCsv(stores, exportContext);
    }, [exportContext, stores]);

    const handleRefresh = async () => {
        const refreshes: Promise<unknown>[] = [refetch()];
        if (cycleId && evidenceStore?.routeItemId) {
            refreshes.push(queryClient.refetchQueries({
                queryKey: ["tracker-evidence", Number(cycleId), evidenceStore.routeItemId],
                exact: true,
                type: "active",
            }));
        }
        await Promise.all(refreshes);
    };
    // Use React Query's dataUpdatedAt (epoch ms) rather than data.updatedAt from the response
    // body, because the server sets Cache-Control + ETags and the HTTP cache replays the stale
    // response body (including its old updatedAt timestamp) on 304 hits.
    const fullyCompleteCount = useMemo(() =>
        stores.filter(s => s.operationalStatus === "COMPLETE" && s.complianceStatus === "COMPLETE" && !(workloadByStore.get(s.storeId)?.pendingCount)).length,
    [stores, workloadByStore]);

    const filtered = useMemo(() => {
        let result = stores;
        switch (statusFilter) {
            case "ALL":
                break;
            case "STARTED":
                result = result.filter(s => s.operationalStatus !== "NOT_STARTED");
                break;
            case "NOT_STARTED":
                result = result.filter(s => s.operationalStatus === "NOT_STARTED");
                break;
            case "OPERATOR_COMPLETE":
                result = result.filter(s => s.operationalStatus === "COMPLETE");
                break;
            case "FULLY_COMPLETE":
                result = result.filter(s => s.operationalStatus === "COMPLETE" && s.complianceStatus === "COMPLETE");
                break;
            case "NEEDS_REVIEW":
                result = result.filter(s => (workloadAvailable ? workloadByStore.get(s.storeId)?.pendingCount ?? 0 : s.pendingReviewCount) > 0);
                break;
            default: {
                const exhaustive: never = statusFilter;
                return exhaustive;
            }
        }
        if (hideAdminComplete) {
            result = result.filter(s => !(s.operationalStatus === "COMPLETE" && s.complianceStatus === "COMPLETE") || (workloadByStore.get(s.storeId)?.pendingCount ?? 0) > 0);
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            result = result.filter(s =>
                s.storeName.toLowerCase().includes(q) ||
                s.storeAddress?.toLowerCase().includes(q) ||
                s.operatorName?.toLowerCase().includes(q) ||
                s.operatorNames?.some(name => name.toLowerCase().includes(q)) ||
                s.sentToOperatorName?.toLowerCase().includes(q) ||
                s.campaigns.some(c => c.campaignName.toLowerCase().includes(q)) ||
                s.campaigns.some(c => c.operatorName?.toLowerCase().includes(q))
            );
        }
        if (awaitingReviewOnly && workloadAvailable) result = result.filter(s => (workloadByStore.get(s.storeId)?.pendingCount ?? 0) > 0);
        return sortStoresForReview(result, workloadByStore, reviewSort);
    }, [stores, search, statusFilter, hideAdminComplete, awaitingReviewOnly, workloadByStore, reviewSort, workloadAvailable]);

    const STATUS_FILTERS: Array<{ value: StoreStatusFilter; label: string }> = [
        { value: "ALL", label: "All" },
        { value: "STARTED", label: "Started" },
        { value: "NOT_STARTED", label: "Not Started" },
        { value: "OPERATOR_COMPLETE", label: "Operator Complete" },
        { value: "FULLY_COMPLETE", label: "Operator + Compliance Complete" },
        { value: "NEEDS_REVIEW", label: "Needs Review" },
    ];

    return (
        <TrackerLayout
            title="Campaign Tracker"
            lastUpdated={lastUpdated}
            onRefresh={() => { void handleRefresh(); }}
            isRefreshing={isFetching}
        >
            <div className="space-y-4">
                {/* Search + filter bar */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                            placeholder="Search stores, campaigns, operators…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="pl-9 h-9"
                        />
                    </div>
                    <div className="flex items-center gap-1 rounded-lg border bg-muted p-1">
                        {STATUS_FILTERS.map(f => (
                            <button
                                key={f.value}
                                type="button"
                                onClick={() => setStatusFilter(f.value)}
                                className={[
                                    "px-3 py-1 text-xs font-medium rounded transition-colors",
                                    statusFilter === f.value
                                        ? "bg-white text-foreground shadow-sm"
                                        : "text-muted-foreground hover:text-foreground"
                                ].join(" ")}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => setHideAdminComplete(h => !h)}
                        className={[
                            "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors",
                            hideAdminComplete
                                ? "bg-green-50 border-green-200 text-green-700"
                                : "bg-background border-border text-muted-foreground hover:text-foreground"
                        ].join(" ")}
                    >
                        <CheckCheck className="h-3.5 w-3.5" />
                        {hideAdminComplete
                            ? `Showing active (${fullyCompleteCount} hidden)`
                            : `Hide complete stores${fullyCompleteCount > 0 ? ` (${fullyCompleteCount})` : ""}`
                        }
                    </button>
                </div>

                {/* Summary cards */}
                {stores.length > 0 && <SummaryCards stores={stores} />}

                {/* Store list */}
                <div className="rounded-lg border bg-white overflow-hidden shadow-sm">
                    {/* Section header */}
                    <div className="flex items-center justify-between gap-3 px-6 py-4 border-b">
                        <h2 className="text-base font-semibold">Store Installations</h2>
                        <div className="flex items-center gap-3">
                            {filtered.length > 0 && stores.length > 0 && (
                                <span className="text-xs text-muted-foreground">
                                    Showing {filtered.length} of {stores.length}
                                </span>
                            )}
                            {storeExportCsv && exportContext && (
                                <TrackerProgressExportButton
                                    csv={storeExportCsv}
                                    filenameStem={buildTrackerExportFilenameStem('stores', exportContext.cycleName)}
                                />
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-5 px-6 py-3 border-b">
                        <label className="flex items-center gap-2 text-sm">
                            Sort by
                            <select aria-label="Sort stores" value={reviewSort}
                                onChange={event => setReviewSort(event.target.value as StoreReviewSort)}
                                className="rounded-md border bg-background px-2 py-1.5 text-sm">
                                <option value="name">Store name</option>
                                <option value="oldest" disabled={!workloadAvailable}>Oldest unreviewed first</option>
                            </select>
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={awaitingReviewOnly} disabled={!workloadAvailable}
                                onChange={event => setAwaitingReviewOnly(event.target.checked)} />
                            Awaiting review only
                        </label>
                    </div>
                    {error && <div role="alert" className="px-6 py-2 text-sm text-destructive">
                        Could not refresh store review information. Last loaded data may be out of date.
                    </div>}
                    {data && !workloadAvailable && <div role="status" className="px-6 py-2 text-sm text-muted-foreground">
                        Review priority information is unavailable. Refresh to try again.
                    </div>}
                    <div className="overflow-x-auto">
                        <div className="min-w-[1210px]">
                            {/* Column headers */}
                            {!isLoading && filtered.length > 0 && (
                                <div className={`${ROW_GRID} px-6 py-2.5 bg-gray-50 border-b text-xs font-semibold text-muted-foreground uppercase tracking-wide`}>
                                    <div>#</div>
                                    <div>Store Name</div>
                                    <div>Operator Status</div>
                                    <div>Compliance Status</div>
                                    <div>Campaign Status</div>
                                    <div className="text-right">Actions</div>
                                </div>
                            )}

                            {isLoading && (
                                <div className="p-8 text-center text-muted-foreground text-sm">
                                    Loading store progress…
                                </div>
                            )}
                            {!isLoading && !cycleId && (
                                <div className="p-8 text-center text-muted-foreground text-sm">
                                    Select a retailer and cycle to view store progress.
                                </div>
                            )}
                            {!isLoading && cycleId && filtered.length === 0 && (
                                <div className="p-8 text-center text-muted-foreground text-sm">
                                    {stores.length === 0 ? "No stores found for this cycle." : "No stores match your filter."}
                                </div>
                            )}
                            {!isLoading && filtered.length > 0 && (
                                <StoreReviewPresenceTooltipRoot>
                                    <div className="px-2">
                                        {filtered.map((store, i) => (
                                            <StoreRow
                                                key={store.storeId}
                                                store={store}
                                                reviewWorkload={workloadByStore.get(store.storeId)}
                                                reviewNow={reviewAgeNow}
                                                reviewHref={storeReviewHref({ retailerId, cycleId: cycleId!, storeId: store.storeId,
                                                    storeName: store.storeName, campaignId: searchParams.get('campaignId'), taskDefinitionIds })}
                                                index={i}
                                                cycleId={cycleId}
                                                retailerId={retailerId}
                                                onViewEvidence={setEvidenceStore}
                                                showReviewPresence={reviewPresenceFlagOn}
                                                storeReviewers={presenceByStore.get(store.storeId) ?? []}
                                            />
                                        ))}
                                    </div>
                                </StoreReviewPresenceTooltipRoot>
                            )}
                        </div>
                    </div>
                </div>

            </div>

            {/* Evidence Drawer */}
            {evidenceStore?.routeItemId && cycleId && (
                <EvidenceDrawer
                    cycleId={Number(cycleId)}
                    routeItemId={evidenceStore.routeItemId}
                    open={!!evidenceStore}
                    onOpenChange={(open) => { if (!open) setEvidenceStore(null); }}
                />
            )}
        </TrackerLayout>
    );
}
